import * as Xstate from "@xstate/react";
import classNames from "classnames";
import moment from "moment";
import React, {
  FC,
  KeyboardEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineMinusCircle } from "react-icons/hi";
import {
  LuCornerUpLeft,
  LuMic,
  LuPaperclip,
  LuSend,
  LuSquare,
  LuX,
} from "react-icons/lu";
import { Mention, MentionsInput } from "react-mentions";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Pages } from "../../../../../../app/constants/routes";
import { generateRoutePath } from "../../../../../../app/constants/routes/utils";
import { GlobalContext } from "../../../../../../app/stateMachines/GlobalContext";
import { AvatarPlaceholder } from "../../../../../../components/avatar/AvatarPlaceholder";
import { LottieLoading } from "../../../../../../components/graphics/LottieLoading";
import ConfirmModal from "../../../../../../components/modal/ConfirmModal";
import PreviewFile from "../../../../../../components/modal/PreviewFile";
import ContactNoteSvg from "../../../../../../components/svg/ContactNoteSvg";
import { ContactsIcon } from "../../../../../../components/svg/navIcons/index-svg";
import { getInitials } from "../../../../../../constants/constants";
import { useAppSelector } from "../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../sessionStorage/sessionStorage";
import { useGetWorkFilesListQuery } from "../../../../../../slices/DocumentsSlice";
import {
  useAddWorkFileMutation,
  useGetWorkFilesQuery,
  useLazyGetPreviewWorkFileQuery,
  useLazyGetWorkFilesQuery,
} from "../../../../../../slices/FilesSlice";
import {
  useGetUsersQuery,
  useMentionNotificationMutation,
} from "../../../../../../slices/UserSlice";
import {
  useAddWorkNoteMutation,
  useDeleteParticipantsMutation,
  useDeleteWorkNoteMutation,
  useGetParticipantsQuery,
  useGetWorkNotesQuery,
  useUpdateWorkNoteMutation,
} from "../../../../../../slices/WorkNotesSlice";
import { IUser, IWorkNote } from "../../../../../../types/types";
import { WriteWithAi } from "../../../../../ai/WriteWithAi";
import { renderRichText } from "../../../../../contacts/utils/parseNote";
import { useFileOpener } from "../../../../../files/useFileOpener";
import { downloadWorkFile, FileNoteCard, NoteFilesMenu } from "./NoteFiles";
import {
  editableNoteText,
  keepNoteMarkers,
  noteSnippet,
  parseReplyNote,
  replyNoteText,
  withReplyMention,
} from "./noteReply.utils";
import { appendDictation, useDictation } from "./useDictation";
import {
  fileNoteText,
  findUploadedFile,
  notesInChatOrder,
  parseFileNote,
  sameDay,
  wasEdited,
} from "./workNotesChat.utils";

const composerStyle = {
  control: { backgroundColor: "transparent", fontSize: 14 },
  "&multiLine": {
    control: { fontFamily: "inherit", minHeight: 44 },
    highlighter: { padding: "11px 14px", fontSize: 14 },
    input: {
      padding: "11px 14px",
      border: "none",
      outline: "none",
      fontFamily: "inherit",
      fontSize: 14,
      maxHeight: 140,
      overflowY: "auto" as const,
    },
  },
  suggestions: {
    list: {
      backgroundColor: "white",
      border: "1px solid #E5E7EB",
      fontSize: 14,
      borderRadius: 8,
      maxHeight: 180,
      overflowY: "auto" as const,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    },
    item: {
      padding: "6px 12px",
      "&focused": { backgroundColor: "#F4F2FE" },
    },
  },
};

const fullName = (note: IWorkNote) =>
  `${note.createdByUser?.userProfile?.firstName ?? ""} ${
    note.createdByUser?.userProfile?.lastName ?? ""
  }`.trim();

/**
 * Work item Notes as a chat: messages fill the panel (oldest at the top) and
 * a message box sits at the bottom. Enter sends, Shift+Enter adds a line.
 */
export const WorkNotesChat: FC = () => {
  const { workId = "" } = useParams();
  const { user } = useAppSelector((state) => state.appReducer);
  const { userInfoService } = useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const { data: workNotes, isLoading } = useGetWorkNotesQuery({ workId });
  const { data: participants = [] } = useGetParticipantsQuery({ workId });
  const { data: users } = useGetUsersQuery(getLinkedFirm()?.orgId || "");

  const [addWorkNote, { isLoading: isSending }] = useAddWorkNoteMutation();
  const [deleteWorkNote, { isLoading: isDeleting }] =
    useDeleteWorkNoteMutation();
  const [updateWorkNote, { isLoading: isUpdating }] =
    useUpdateWorkNoteMutation();
  const [sendNotifications] = useMentionNotificationMutation();
  const [deleteParticipant, { isLoading: isRemovingParticipant }] =
    useDeleteParticipantsMutation();
  const { data: workFiles = [] } = useGetWorkFilesQuery(workId);
  // The header list = the Files tab's list (client files filed to this work too; NOTE-016).
  const { data: workFileList = [], refetch: refetchFileList } =
    useGetWorkFilesListQuery(workId, { skip: !workId });
  const menuFiles = useMemo(
    () =>
      workFileList.map((item) => ({
        id: `${item.kind}:${item.id}`,
        name: item.name,
        date: item.addedAt,
        item,
      })),
    [workFileList],
  );
  const { openFile: openListFile, preview: listFilePreview } = useFileOpener(
    (message: string) => {
      if (message) toast.error(message);
    },
  );
  const [fetchWorkFiles] = useLazyGetWorkFilesQuery();
  const [addWorkFile] = useAddWorkFileMutation();
  const [getFileUrl] = useLazyGetPreviewWorkFileQuery();
  const [getPreview, { data: preview, isFetching: isLoadingPreview }] =
    useLazyGetPreviewWorkFileQuery();
  const navigate = useNavigate();

  const [draft, setDraft] = useState("");
  const [mentions, setMentions] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  // The note being answered (Reply under a bubble), shown above the box.
  const [replyTo, setReplyTo] = useState<IWorkNote | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deleting, setDeleting] = useState<IWorkNote | null>(null);
  const [removeParticipant, setRemoveParticipant] = useState<{
    id: string;
    userId: string;
    user: IUser;
  } | null>(null);

  const [uploading, setUploading] = useState(0);
  const [previewName, setPreviewName] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout>>();
  const notes = useMemo(() => notesInChatOrder(workNotes), [workNotes]);
  // Only the author may edit a note; Admins (Settings > Team) may still delete
  // anyone's note (Roadmap 1870, owner 2026-10-22).
  const isAdmin = user?.userRoleEnum?.userRole === "Admin";
  const myIds = [user?.id, userInfoByEmail?.id]
    .filter(Boolean)
    .map((id) => `${id}`.toLowerCase());

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [notes.length]);

  useEffect(() => () => clearTimeout(highlightTimer.current), []);

  const notesById = useMemo(
    () => new Map(notes.map((note) => [`${note.id}`.toLowerCase(), note])),
    [notes],
  );
  const findNote = (id: string | null) =>
    id ? notesById.get(id.toLowerCase()) : undefined;

  // The author's name as the @mention list shows it (so a mention matches).
  const authorName = (note: IWorkNote) => {
    const author = users?.find(
      (item) =>
        `${item.id}`.toLowerCase() === `${note.createdByUserId}`.toLowerCase(),
    );
    return author
      ? `${author.userProfile?.firstName ?? ""} ${
          author.userProfile?.lastName ?? ""
        }`.trim()
      : fullName(note);
  };

  const userOptions = useMemo(
    () =>
      users?.map((item) => ({
        id: `${item.id}`,
        display: `${item?.userProfile?.firstName} ${item?.userProfile?.lastName}`,
        avatar: getInitials(
          item?.userProfile?.firstName,
          item?.userProfile?.lastName,
        ),
      })) || [],
    [users],
  );

  const dictation = useDictation((spoken) =>
    setDraft((current) => appendDictation(current, spoken)),
  );

  const startEdit = (note: IWorkNote) => {
    setEditingId(note.id);
    // The hidden reply link isn't shown while editing; it is kept on save.
    setEditText(editableNoteText(note.note));
  };

  const saveEdit = async (note: IWorkNote) => {
    const text = editText.trim();
    if (!text || isUpdating) return;
    const updated = keepNoteMarkers(note.note, text);
    if (updated !== note.note) {
      await updateWorkNote({ ...note, note: updated }).unwrap();
    }
    setEditingId(null);
  };

  const startReply = (note: IWorkNote) => {
    setReplyTo(note);
    setTimeout(() => composerRef.current?.focus(), 0);
  };

  // Scrolls to the note a reply quotes and highlights it for a moment.
  const showOriginal = (id: string) => {
    const element = listRef.current?.querySelector<HTMLElement>(
      `[data-note-id="${CSS.escape(id)}"]`,
    );
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(id);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 1800);
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || isSending) return;
    dictation.stop();
    let note = text;
    let mentioned = mentions;
    if (replyTo) {
      // Replying to someone else @mentions them, so they are notified.
      const authorId = `${replyTo.createdByUserId}`;
      const isSelf = myIds.includes(authorId.toLowerCase());
      if (!isSelf) {
        note = withReplyMention(
          note,
          authorName(replyTo),
          mentions.some((id) => id.toLowerCase() === authorId.toLowerCase()),
        );
        mentioned = [...mentions, authorId];
      }
      note = replyNoteText(replyTo.id, note);
    }
    await addWorkNote({
      title: "",
      note,
      createdByUserRelayId: `${userInfoByEmail?.id}`,
      workId,
    }).unwrap();
    setDraft("");
    setReplyTo(null);
    notify(mentioned);
    setMentions([]);
  };

  const notify = (mentioned: string[]) => {
    const recipients = [
      ...new Set([...mentioned, ...participants.map((item) => item.userId)]),
    ].filter((id) => id !== user?.id);
    if (recipients.length) {
      sendNotifications({
        workId,
        userId: `${user?.id}`,
        userIds: recipients,
      });
    }
  };

  // Each attached file goes to the work item's Files, then into the chat.
  const attachFiles = async (files: File[]) => {
    const before = workFiles.map((item) => item.id);
    for (const file of files) {
      setUploading((count) => count + 1);
      try {
        const formData = new FormData();
        formData.append("Documents", file);
        formData.append("workId", workId);
        formData.append("assignedUserRelayId", `${userInfoByEmail?.id}`);
        formData.append("notes", "");
        await addWorkFile(formData).unwrap();
        // The upload doesn't return the new file, so look for it in the list
        // (a refresh may already be under way; give it a moment if needed).
        let uploaded;
        for (let attempt = 0; attempt < 6 && !uploaded; attempt += 1) {
          if (attempt) await new Promise((done) => setTimeout(done, 500));
          uploaded = findUploadedFile(
            before,
            (await fetchWorkFiles(workId).unwrap()) ?? [],
            file.name,
          );
        }
        if (!uploaded) throw new Error("Uploaded file not found");
        before.push(uploaded.id);
        await addWorkNote({
          title: "",
          note: fileNoteText(uploaded.originalFileName, uploaded.id),
          createdByUserRelayId: `${userInfoByEmail?.id}`,
          workId,
        }).unwrap();
        notify([]);
        void refetchFileList();
      } catch {
        toast(`${file.name} couldn't be uploaded.`, { type: "error" });
      } finally {
        setUploading((count) => count - 1);
      }
    }
  };

  const openFile = (fileId: string, fileName: string) => {
    setPreviewName(fileName);
    void getPreview(fileId);
  };

  // Enter sends; while the @mention list is open, Enter picks a person instead
  // (react-mentions marks that key press as handled).
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && replyTo && !event.defaultPrevented) {
      event.preventDefault();
      setReplyTo(null);
      return;
    }
    if (event.key === "Enter" && !event.shiftKey && !event.defaultPrevented) {
      event.preventDefault();
      void send();
    }
  };

  return (
    <>
      <div
        className={
          "flex h-[calc(100vh-210px)] min-h-[480px] flex-col overflow-hidden rounded-[10px] bg-white shadow-box"
        }>
        <div
          className={
            "relative flex h-[60px] shrink-0 items-center justify-between border-b border-gray-200 px-5"
          }>
          <div className={"text-base font-semibold"}>Notes</div>
          <div className={"flex items-center gap-1.5"}>
            <NoteFilesMenu
              files={menuFiles}
              onOpen={(file) => openListFile(file.item)}
              onUpload={() => fileInputRef.current?.click()}
              onViewAll={() =>
                navigate(
                  generateRoutePath([
                    Pages.work.path,
                    workId,
                    Pages.work.children.files.path,
                  ]),
                )
              }
            />
            <div className={"dropdown dropdown-end"}>
              <button
                type={"button"}
                aria-label={"Participants"}
                title={"Participants"}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-md hover:bg-[#F4F2FE]"
                }>
                <ContactsIcon className={"h-5 w-5 text-[#7c66f0]"} />
              </button>
              <div
                className={
                  "dropdown-content z-30 max-h-[220px] w-[250px] overflow-y-auto rounded-[8px] border border-gray-200 bg-white py-3 text-sm shadow-box"
                }>
                <div className={"px-5"}>
                  <div className={"text-sm font-bold"}>Participants</div>
                  <div className={"text-xs text-gray-500"}>
                    Use @mention to add participants
                  </div>
                </div>
                {participants.map((item) => (
                  <div
                    key={item.id}
                    className={
                      "mt-2 flex items-center justify-between gap-2 border-t border-gray-100 px-5 pt-2"
                    }>
                    <span
                      className={
                        "truncate text-sm font-semibold text-gray-600"
                      }>
                      {item.user?.userProfile?.firstName}{" "}
                      {item.user?.userProfile?.lastName}
                    </span>
                    <HiOutlineMinusCircle
                      onMouseDown={() => setRemoveParticipant(item)}
                      className={
                        "h-5 w-5 shrink-0 cursor-pointer text-[#7c66f0]"
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          ref={listRef}
          className={"min-h-0 flex-1 overflow-y-auto px-4 py-4"}>
          {isLoading ? (
            <LottieLoading />
          ) : !notes.length ? (
            <div
              className={
                "flex h-full flex-col items-center justify-center gap-2 text-center"
              }>
              <ContactNoteSvg />
              <div className={"text-sm font-semibold text-[#2B333C]"}>
                No notes yet
              </div>
              <div className={"max-w-[220px] text-xs text-gray-500"}>
                Write the first note below. Use @ to notify a teammate.
              </div>
            </div>
          ) : (
            <div className={"flex flex-col gap-3"}>
              {notes.map((note, index) => {
                const mine = myIds.includes(
                  `${note.createdByUserId}`.toLowerCase(),
                );
                const shared = parseFileNote(note.note);
                const replyId = parseReplyNote(note.note);
                const original = findNote(replyId);
                const sharedFile =
                  shared && workFiles.find((file) => file.id === shared.id);
                const showDay =
                  index === 0 ||
                  !sameDay(notes[index - 1].createdAt, note.createdAt);
                return (
                  <React.Fragment key={note.id}>
                    {showDay && (
                      <div
                        className={
                          "my-1 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400"
                        }>
                        {moment(note.createdAt).format("MMM D, YYYY")}
                      </div>
                    )}
                    <div
                      data-note-id={note.id}
                      className={classNames(
                        "group flex items-end gap-2 rounded-xl transition-colors duration-500",
                        mine && "flex-row-reverse",
                        highlightId === note.id && "bg-[#EEEAFF]",
                      )}>
                      {!mine && (
                        <AvatarPlaceholder
                          size={"2xs"}
                          label={getInitials(
                            note.createdByUser?.userProfile?.firstName,
                            note.createdByUser?.userProfile?.lastName,
                          )}
                        />
                      )}
                      <div
                        className={classNames(
                          // min-w-0: long words / links wrap instead of widening the bubble (WITEM-046).
                          "flex min-w-0 max-w-[85%] flex-col",
                          mine ? "items-end" : "items-start",
                        )}>
                        <div
                          className={
                            "mb-1 flex items-center gap-1.5 text-[11px] text-gray-500"
                          }>
                          {!mine && (
                            <span className={"font-semibold text-gray-600"}>
                              {fullName(note)}
                            </span>
                          )}
                          <span>{moment(note.createdAt).format("h:mm A")}</span>
                        </div>
                        {editingId === note.id ? (
                          <div
                            className={
                              "w-[280px] max-w-full rounded-2xl border-[1.5px] border-[#6B4EFF] bg-white p-2"
                            }>
                            <textarea
                              aria-label={"Edit note"}
                              autoFocus
                              value={editText}
                              onChange={(event) =>
                                setEditText(event.target.value)
                              }
                              onKeyDown={(event) => {
                                if (event.key === "Escape") setEditingId(null);
                                if (event.key === "Enter" && !event.shiftKey) {
                                  event.preventDefault();
                                  void saveEdit(note);
                                }
                              }}
                              rows={3}
                              className={
                                "w-full resize-none border-none text-sm text-[#1F2433] outline-none"
                              }
                            />
                            <div className={"flex justify-end gap-2"}>
                              <button
                                type={"button"}
                                onClick={() => setEditingId(null)}
                                className={
                                  "h-8 rounded-md px-3 text-xs font-semibold text-gray-600 hover:bg-gray-100"
                                }>
                                Cancel
                              </button>
                              <button
                                type={"button"}
                                disabled={!editText.trim() || isUpdating}
                                onClick={() => void saveEdit(note)}
                                className={
                                  "h-8 rounded-md bg-[#6B4EFF] px-3 text-xs font-bold text-white disabled:bg-[#C9CDD6]"
                                }>
                                Save
                              </button>
                            </div>
                          </div>
                        ) : shared ? (
                          <div
                            className={classNames(
                              "rounded-2xl p-1.5",
                              mine
                                ? "rounded-br-md bg-[#6B4EFF] text-white"
                                : "rounded-bl-md bg-[#F2F3F7] text-[#1F2433]",
                            )}>
                            <FileNoteCard
                              name={shared.name}
                              file={sharedFile || undefined}
                              mine={mine}
                              onOpen={() => openFile(shared.id, shared.name)}
                              onDownload={() =>
                                void downloadWorkFile(
                                  getFileUrl,
                                  shared.id,
                                  shared.name,
                                )
                              }
                            />
                          </div>
                        ) : (
                          <div
                            className={classNames(
                              "min-w-0 max-w-full rounded-2xl px-3.5 py-2 text-sm [overflow-wrap:anywhere]",
                              mine
                                ? "rounded-br-md bg-[#6B4EFF] text-white"
                                : "rounded-bl-md bg-[#F2F3F7] text-[#1F2433]",
                            )}>
                            {replyId && (
                              <ReplyQuote
                                mine={mine}
                                name={original ? fullName(original) : ""}
                                snippet={
                                  original ? noteSnippet(original.note, 90) : ""
                                }
                                onClick={
                                  original
                                    ? () => showOriginal(original.id)
                                    : undefined
                                }
                              />
                            )}
                            {note.title && (
                              <div
                                className={
                                  "mb-0.5 font-bold [overflow-wrap:anywhere]"
                                }>
                                {note.title}
                              </div>
                            )}
                            <div
                              className={classNames(
                                "rich-text-content whitespace-pre-wrap break-words [overflow-wrap:anywhere] [&_*]:max-w-full [&_*]:[overflow-wrap:anywhere]",
                                mine && "[&_*]:!text-white",
                              )}>
                              {renderRichText(note.note)}
                            </div>
                          </div>
                        )}
                        {editingId !== note.id && (
                          <div
                            className={
                              "mt-1 flex items-center gap-2 text-[11px] font-semibold text-gray-400"
                            }>
                            {wasEdited(note) && (
                              <span className={"font-normal"}>Edited</span>
                            )}
                            <button
                              type={"button"}
                              onClick={() => startReply(note)}
                              className={"hover:text-[#6B4EFF]"}>
                              Reply
                            </button>
                            {mine && !shared && (
                              <button
                                type={"button"}
                                onClick={() => startEdit(note)}
                                className={"hover:text-[#6B4EFF]"}>
                                Edit
                              </button>
                            )}
                            {(mine || isAdmin) && (
                              <button
                                type={"button"}
                                onClick={() => setDeleting(note)}
                                className={"hover:text-[#E5484D]"}>
                                Delete
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        <div className={"shrink-0 border-t border-gray-200 p-3"}>
          <div
            className={
              "rounded-xl border-[1.5px] border-[#DCE2EA] bg-white focus-within:border-[#6B4EFF]"
            }>
            {replyTo && (
              <div
                className={
                  "mx-2 mt-2 flex items-start gap-2 rounded-lg border-l-[3px] border-[#6B4EFF] bg-[#F4F2FE] py-1.5 pl-2.5 pr-1 text-xs text-[#1F2433]"
                }>
                <LuCornerUpLeft
                  size={14}
                  className={"mt-0.5 shrink-0 text-[#6B4EFF]"}
                />
                <div className={"min-w-0 flex-1"}>
                  <span className={"font-semibold"}>
                    Replying to {fullName(replyTo) || "this note"}:
                  </span>{" "}
                  <span className={"break-words text-gray-600"}>
                    {noteSnippet(replyTo.note)}
                  </span>
                </div>
                <button
                  type={"button"}
                  aria-label={"Cancel reply"}
                  title={"Cancel reply (Esc)"}
                  onClick={() => setReplyTo(null)}
                  className={
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-white hover:text-[#1F2433]"
                  }>
                  <LuX size={14} />
                </button>
              </div>
            )}
            {/* Text on its own full-width line, buttons in a row under it: with
                AI, files, dictation and Send beside it the box was only ~50-90px wide. */}
            <div className={"min-w-0"} onKeyDown={onKeyDown}>
              <MentionsInput
                a11ySuggestionsListLabel={"Suggested mentions"}
                inputRef={composerRef}
                placeholder={
                  replyTo ? "Write a reply…" : "Write a note… (@ to mention)"
                }
                style={composerStyle}
                value={draft}
                allowSuggestionsAboveCursor={true}
                forceSuggestionsAboveCursor={true}
                onChange={(event) => setDraft(event.target.value)}>
                <Mention
                  trigger={"@"}
                  onAdd={(id) => setMentions((prev) => [...prev, `${id}`])}
                  data={userOptions}
                  markup={"@[__display__]"}
                  style={{ backgroundColor: "#E9E4FF" }}
                />
              </MentionsInput>
            </div>
            <div className={"flex items-center justify-end gap-1 px-1"}>
              <WriteWithAi
                purpose={"note"}
                format={"text"}
                compact
                placement={"top"}
                getHtml={() => draft}
                onUseHtml={setDraft}
                workId={workId}
              />
              <input
                ref={fileInputRef}
                type={"file"}
                multiple
                hidden
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                  if (files.length) void attachFiles(files);
                }}
              />
              <button
                type={"button"}
                aria-label={"Attach a file"}
                title={"Attach a file (saved to Files)"}
                disabled={uploading > 0}
                onClick={() => fileInputRef.current?.click()}
                className={
                  "my-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-[#F4F2FE] hover:text-[#6B4EFF] disabled:opacity-50"
                }>
                <LuPaperclip size={18} />
              </button>
              {dictation.supported && (
                <button
                  type={"button"}
                  aria-label={
                    dictation.isListening ? "Stop dictation" : "Dictate a note"
                  }
                  title={
                    dictation.isListening
                      ? "Stop dictation"
                      : "Dictate (speak instead of typing)"
                  }
                  onClick={
                    dictation.isListening ? dictation.stop : dictation.start
                  }
                  className={classNames(
                    "my-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    dictation.isListening
                      ? "animate-pulse bg-[#FDECEC] text-[#E5484D]"
                      : "text-gray-500 hover:bg-[#F4F2FE] hover:text-[#6B4EFF]",
                  )}>
                  {dictation.isListening ? (
                    <LuSquare size={14} fill={"currentColor"} />
                  ) : (
                    <LuMic size={18} />
                  )}
                </button>
              )}
              <button
                type={"button"}
                aria-label={"Send note"}
                onClick={() => void send()}
                disabled={!draft.trim() || isSending}
                className={
                  "m-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#6B4EFF] text-white disabled:bg-[#C9CDD6]"
                }>
                <LuSend size={16} />
              </button>
            </div>
          </div>
          <div className={"mt-1.5 px-1 text-[11px] text-gray-400"}>
            {dictation.isListening ? (
              <span className={"font-semibold text-[#E5484D]"}>
                Listening… speak, then press the red button to stop
              </span>
            ) : dictation.error ? (
              <span className={"text-[#E5484D]"}>{dictation.error}</span>
            ) : uploading > 0 ? (
              <span className={"font-semibold text-[#6B4EFF]"}>
                Uploading {uploading === 1 ? "file" : `${uploading} files`}…
              </span>
            ) : (
              "Enter to send · Shift+Enter for a new line · Attached files are saved to Files"
            )}
          </div>
        </div>
      </div>

      {deleting && (
        <ConfirmModal
          confirm={() =>
            deleteWorkNote(deleting)
              .unwrap()
              .then(() => setDeleting(null))
          }
          close={() => setDeleting(null)}
          title={""}
          message={
            <div className={"text-base text-gray-800"}>
              Delete this note? This can&apos;t be undone.
            </div>
          }
          isLoading={isDeleting}
          buttonsClasses={"flex-row-reverse gap-4"}
        />
      )}
      {listFilePreview}
      {previewName && (
        <PreviewFile
          close={() => setPreviewName(null)}
          file={isLoadingPreview ? "" : preview?.documentUrl || ""}
          fileName={previewName}
          isLoading={isLoadingPreview}
        />
      )}
      {removeParticipant && (
        <ConfirmModal
          confirm={() =>
            deleteParticipant({ workId, id: removeParticipant.id })
              .unwrap()
              .then(() => setRemoveParticipant(null))
          }
          close={() => setRemoveParticipant(null)}
          title={""}
          message={
            <div className={"text-base text-gray-800"}>
              This user will no longer receive notifications regarding this
              conversation. Notifications can be resumed by using the @mention
              feature.
            </div>
          }
          isLoading={isRemovingParticipant}
          buttonsClasses={"flex-row-reverse gap-4"}
        />
      )}
    </>
  );
};

/** The quoted note at the top of a reply bubble. */
const ReplyQuote: FC<{
  mine: boolean;
  name: string;
  snippet: string;
  onClick?: () => void;
}> = ({ mine, name, snippet, onClick }) => {
  const look = classNames(
    "mb-1.5 block w-full rounded-lg border-l-[3px] px-2 py-1 text-left text-xs",
    mine
      ? "border-white/70 bg-white/15 text-white"
      : "border-[#6B4EFF] bg-white text-[#1F2433]",
  );
  if (!onClick) {
    return (
      <div className={classNames(look, "italic opacity-80")}>
        Original note was deleted
      </div>
    );
  }
  return (
    <button
      type={"button"}
      onClick={onClick}
      title={"Show the original note"}
      className={classNames(look, "hover:opacity-90")}>
      <div className={"font-semibold"}>{name || "Note"}</div>
      <div
        className={classNames(
          "line-clamp-2 break-words",
          mine ? "text-white/85" : "text-gray-600",
        )}>
        {snippet}
      </div>
    </button>
  );
};
